import { useContext, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { ChatMessage } from "@planzone/dto";
import { CoreContext } from "../context";
import { useChatStreamContext } from "./context";

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

interface ViewModel {
  messages: ChatMessage[];
  isSubmitPending: boolean;
  isSubmitFailure: boolean;
  isStreaming: boolean;
  statusMessage: string | undefined;
  streamError: unknown;
  sendMessage(question: string): void;
  cancelJob(): void;
  reset(): void;
}

// The streaming buffer is kept in local hook state on purpose: dispatching a
// Redux action per received token would be far too costly (see ticket
// ai-assistant-integration.md, "Décisions"). Only structural changes
// (conversation list refresh) go through core/Redux.
function useChatStream(conversationId?: string): ViewModel {
  const { core, selector } = useContext(CoreContext);
  const streamJob = useChatStreamContext();

  const isSubmitPending = useSelector(selector.isSubmitMessageRequestPending);
  const isSubmitFailure = useSelector(selector.isSubmitMessageRequestFailure);
  const chatJob = useSelector(selector.getChatJob);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string>();
  const [streamError, setStreamError] = useState<unknown>();

  const startedJobIdRef = useRef<string | undefined>(undefined);
  const conversationIdRef = useRef<string | undefined>(conversationId);
  const abortControllerRef = useRef<AbortController | undefined>(undefined);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const lastEventIdRef = useRef<string | undefined>(undefined);

  function appendToken(token: string) {
    setMessages((previousMessages) => {
      const lastMessage = previousMessages[previousMessages.length - 1];
      return [
        ...previousMessages.slice(0, -1),
        { ...lastMessage, content: lastMessage.content + token },
      ];
    });
  }

  function stopStreaming() {
    abortControllerRef.current?.abort();
    if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    abortControllerRef.current = undefined;
    retryTimeoutRef.current = undefined;
  }

  function startStream(jobId: string, attempt: number) {
    setIsStreaming(true);
    setStreamError(undefined);

    const controller = new AbortController();
    abortControllerRef.current = controller;
    let serverErrorMessage: string | undefined;

    streamJob(
      jobId,
      {
        onToken: appendToken,
        onStatus: setStatusMessage,
        onError: (message) => {
          serverErrorMessage = message;
        },
        onEventId: (id) => {
          lastEventIdRef.current = id;
        },
      },
      lastEventIdRef.current,
      controller.signal,
    )
      .then(() => {
        if (controller.signal.aborted) return;
        setIsStreaming(false);
        setStatusMessage(undefined);
        if (serverErrorMessage) setStreamError(serverErrorMessage);
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        if (attempt < MAX_RETRIES) {
          retryTimeoutRef.current = setTimeout(
            () => startStream(jobId, attempt + 1),
            RETRY_DELAY_MS * 2 ** attempt,
          );
        } else {
          setIsStreaming(false);
          setStreamError(error);
        }
      });
  }

  useEffect(() => {
    if (!chatJob.jobId || chatJob.jobId === startedJobIdRef.current) return;

    startedJobIdRef.current = chatJob.jobId;
    conversationIdRef.current = chatJob.conversationId;
    lastEventIdRef.current = undefined;
    setMessages((previousMessages) => [
      ...previousMessages,
      { role: "bot", content: "" },
    ]);
    core.retrieveConversations();
    startStream(chatJob.jobId, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatJob.jobId]);

  useEffect(() => stopStreaming, []);

  const isFirstRenderRef = useRef(true);
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  function sendMessage(question: string) {
    setMessages((previousMessages) => [
      ...previousMessages,
      { role: "user", content: question },
    ]);
    core.submitMessage({
      question,
      conversationId: conversationIdRef.current,
    });
  }

  function cancelJob() {
    stopStreaming();
    if (startedJobIdRef.current) {
      core.cancelJob({ jobId: startedJobIdRef.current });
    }
    setIsStreaming(false);
    setStatusMessage(undefined);
  }

  function reset() {
    stopStreaming();
    startedJobIdRef.current = undefined;
    conversationIdRef.current = conversationId;
    lastEventIdRef.current = undefined;
    setMessages([]);
    setIsStreaming(false);
    setStatusMessage(undefined);
    setStreamError(undefined);
  }

  return {
    messages,
    isSubmitPending,
    isSubmitFailure,
    isStreaming,
    statusMessage,
    streamError,
    sendMessage,
    cancelJob,
    reset,
  };
}

export { useChatStream };
